// src/utils/agriculturalCategories.js
const DEFAULT_CATEGORY_COLOR = 'bg-gray-100 text-gray-800 dark:bg-gray-900/40 dark:text-gray-200';

const CATEGORY_COLOR_MAP = {
  cereals: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200',
  legumes: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-200',
  fruits: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200',
  vegetables: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200',
  tubers: 'bg-orange-100 text-orange-800 dark:bg-orange-900/40 dark:text-orange-200',
  oilseeds: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-200',
  nuts: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200',
  spices: 'bg-rose-100 text-rose-800 dark:bg-rose-900/40 dark:text-rose-200',
  herbs: 'bg-lime-100 text-lime-800 dark:bg-lime-900/40 dark:text-lime-200',
  beverages: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200',
  fibers: 'bg-gray-100 text-gray-800 dark:bg-gray-900/40 dark:text-gray-200',
  forage: 'bg-teal-100 text-teal-800 dark:bg-teal-900/40 dark:text-teal-200',
  plantation: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200',
  pulses: 'bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-200',
  grains: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200',
  mushrooms: 'bg-stone-100 text-stone-800 dark:bg-stone-900/40 dark:text-stone-200',
  medicinal: 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200',
  aromatic: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/40 dark:text-indigo-200',
  tree: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-200',
  aquatic: 'bg-cyan-100 text-cyan-800 dark:bg-cyan-900/40 dark:text-cyan-200',
  flowers: 'bg-pink-100 text-pink-800 dark:bg-pink-900/40 dark:text-pink-200',
  stimulants: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200',
  sugar: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-200',
  bioenergy: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-200'
};

export const AGRICULTURAL_CATEGORIES = [
  {
    id: 'cereals',
    name: 'Cereals',
    description: 'Grass-like plants grown for their edible grains',
    examples: ['Maize', 'Wheat', 'Rice', 'Barley', 'Oats', 'Millet', 'Sorghum', 'Rye', 'Teff', 'Fonio', 'Buckwheat'],
    icon: 'FaWheatAwn'
  },
  {
    id: 'legumes',
    name: 'Legumes',
    description: 'Plants that produce pods with edible seeds',
    examples: ['Beans', 'Peas', 'Lentils', 'Chickpeas', 'Soybeans', 'Groundnuts', 'Cowpea', 'Bambara Groundnut', 'Pigeon Pea', 'Lupins', 'Vetches'],
    icon: 'FaSeedling'
  },
  {
    id: 'fruits',
    name: 'Fruits',
    description: 'Sweet and fleshy products of plants',
    examples: ['Bananas', 'Oranges', 'Mangoes', 'Apples', 'Avocados', 'Pineapples', 'Dates', 'Figs', 'Olives', 'Papaya', 'Guava', 'Passion Fruit', 'Jackfruit', 'Baobab', 'Marula', 'Ackee'],
    icon: 'FaAppleWhole'
  },
  {
    id: 'vegetables',
    name: 'Vegetables',
    description: 'Edible plants or parts of plants',
    examples: ['Tomatoes', 'Cabbages', 'Carrots', 'Onions', 'Spinach', 'Eggplants', 'Okra', 'Collard Greens', 'Amaranth', 'Kale', 'Lettuce', 'Broccoli', 'Cauliflower', 'Garlic', 'Leeks', 'French Beans', 'African Eggplant', 'Watercress'],
    icon: 'FaCarrot'
  },
  {
    id: 'tubers',
    name: 'Tubers & Roots',
    description: 'Underground storage organs',
    examples: ['Potatoes', 'Sweet Potatoes', 'Cassava', 'Yams', 'Taro', 'Beets', 'Arrowroot'],
    icon: 'FaCarrot'
  },
  {
    id: 'oilseeds',
    name: 'Oilseeds',
    description: 'Seeds grown primarily for oil extraction',
    examples: ['Sunflower', 'Sesame', 'Rapeseed', 'Castor', 'Flaxseed', 'Palm Kernels', 'Shea', 'Niger', 'Mustard', 'Safflower'],
    icon: 'FaSunPlantWilt'
  },
  {
    id: 'nuts',
    name: 'Nuts',
    description: 'Hard-shelled fruits or seeds',
    examples: ['Cashews', 'Almonds', 'Walnuts', 'Macadamia', 'Pecans', 'Hazelnuts', 'Kola Nuts', 'Brazil Nuts', 'Pistachios', 'Shea Nuts', 'Betel Nuts'],
    icon: 'FaSeedling'
  },
  {
    id: 'spices',
    name: 'Spices',
    description: 'Aromatic substances used for flavoring',
    examples: ['Pepper', 'Cinnamon', 'Turmeric', 'Ginger', 'Chilies', 'Cloves', 'Vanilla', 'Cardamom', 'Nutmeg', 'Saffron', 'Fenugreek', 'Cumin', 'Grains of Paradise', 'Allspice'],
    icon: 'FaPepperHot'
  },
  {
    id: 'herbs',
    name: 'Herbs',
    description: 'Plants used for culinary, medicinal, or aromatic purposes',
    examples: ['Basil', 'Mint', 'Rosemary', 'Thyme', 'Parsley', 'Coriander', 'Sage', 'Oregano', 'Dill', 'Chives', 'Tarragon'],
    icon: 'FaLeaf'
  },
  {
    id: 'beverages',
    name: 'Beverages',
    description: 'Plants used for making drinks',
    examples: ['Coffee', 'Tea', 'Cocoa', 'Maté', 'Khat', 'Herbal Teas', 'Rooibos', 'Hibiscus', 'Chamomile', 'Yerba Mate'],
    icon: 'FaMugHot'
  },
  {
    id: 'fibers',
    name: 'Fibers',
    description: 'Plants grown for their fibrous materials',
    examples: ['Cotton', 'Jute', 'Hemp', 'Flax', 'Sisal', 'Ramie', 'Kenaf', 'Abaca'],
    icon: 'FaYarnBall'
  },
  {
    id: 'forage',
    name: 'Forage & Fodder',
    description: 'Plants grown for animal feed',
    examples: ['Alfalfa', 'Clover', 'Grasses', 'Sorghum', 'Maize Silage', 'Hay', 'Lucerne', 'Timothy Grass'],
    icon: 'FaLeaf'
  },
  {
    id: 'plantation',
    name: 'Plantation Crops',
    description: 'Large-scale cultivated crops',
    examples: ['Sugarcane', 'Rubber', 'Palm Oil', 'Coconut', 'Banana', 'Tea', 'Tobacco', 'Coffee', 'Cocoa'],
    icon: 'FaTreePalm'
  },
  {
    id: 'pulses',
    name: 'Pulses',
    description: 'Edible seeds of leguminous plants',
    examples: ['Dry Beans', 'Dry Peas', 'Chickpeas', 'Lentils', 'Lupins', 'Vetches', 'Broad Beans', 'Mung Beans', 'Cowpea', 'Pigeon Pea'],
    icon: 'FaSeedling'
  },
  {
    id: 'grains',
    name: 'Grains',
    description: 'Small, hard, dry seeds',
    examples: ['Wheat', 'Rice', 'Maize', 'Barley', 'Oats', 'Rye', 'Quinoa', 'Buckwheat', 'Amaranth', 'Chia', 'Triticale'],
    icon: 'FaWheatAwn'
  },
  {
    id: 'mushrooms',
    name: 'Mushrooms',
    description: 'Fungal growths cultivated for food',
    examples: ['Button Mushrooms', 'Oyster Mushrooms', 'Shiitake', 'Portobello', 'Truffles', 'Enoki', 'Maitake', 'Morels', 'Chanterelles'],
    icon: 'FaMushroom'
  },
  {
    id: 'medicinal',
    name: 'Medicinal Plants',
    description: 'Plants with therapeutic properties',
    examples: ['Aloe Vera', 'Ginseng', 'Echinacea', 'Ginkgo', 'Turmeric', 'Garlic', 'Moringa', 'Artemisia', 'Pyrethrum', 'Valerian', "St. John's Wort"],
    icon: 'FaPills'
  },
  {
    id: 'aromatic',
    name: 'Aromatic Plants',
    description: 'Plants with strong fragrances',
    examples: ['Lavender', 'Rosemary', 'Lemongrass', 'Patchouli', 'Vetiver', 'Jasmine', 'Eucalyptus', 'Peppermint', 'Spearmint'],
    icon: 'FaLeaf'
  },
  {
    id: 'tree',
    name: 'Tree Crops',
    description: 'Perennial woody plants',
    examples: ['Fruit Trees', 'Nut Trees', 'Timber Trees', 'Rubber Trees', 'Oil Palms', 'Olive Trees', 'Date Palms'],
    icon: 'FaTree'
  },
  {
    id: 'aquatic',
    name: 'Aquatic Crops',
    description: 'Plants grown in water environments',
    examples: ['Seaweed', 'Watercress', 'Lotus', 'Water Spinach', 'Taro', 'Rice', 'Cranberry', 'Wasabi'],
    icon: 'FaWater'
  },
  {
    id: 'flowers',
    name: 'Flowers & Ornamentals',
    description: 'Plants grown for decorative purposes',
    examples: ['Roses', 'Tulips', 'Lilies', 'Chrysanthemums', 'Gerbera', 'Carnations', 'Orchids', 'Sunflowers', 'Pyrethrum', 'Cut Flowers', 'Daisies'],
    icon: 'FaLeaf'
  },
  {
    id: 'stimulants',
    name: 'Stimulant Crops',
    description: 'Plants producing stimulating compounds',
    examples: ['Tobacco', 'Khat', 'Kola Nut', 'Guarana', 'Betel Nut'],
    icon: 'FaLeaf'
  },
  {
    id: 'sugar',
    name: 'Sugar Crops',
    description: 'Plants grown for sugar production',
    examples: ['Sugarcane', 'Sugar Beet', 'Sweet Sorghum'],
    icon: 'FaLeaf'
  },
  {
    id: 'bioenergy',
    name: 'Bioenergy Crops',
    description: 'Plants for biofuel production',
    examples: ['Jatropha', 'Switchgrass', 'Miscanthus', 'Camelina'],
    icon: 'FaLeaf'
  }
];

export const AGRICULTURAL_MACRO_CATEGORIES = [
  {
    id: 'vegetables-tubers',
    name: 'Vegetables & Tubers',
    icon: 'Sprout',
    groups: [
      { id: 'fresh-vegetables', name: 'Fresh Vegetables', categoryIds: ['vegetables'] },
      { id: 'roots-tubers', name: 'Roots & Tubers', categoryIds: ['tubers'] },
      { id: 'specialty-mushrooms', name: 'Specialty Mushrooms', categoryIds: ['mushrooms'] }
    ]
  },
  {
    id: 'grains-pulses-cereals',
    name: 'Grains, Pulses & Cereals',
    icon: 'BarChart3',
    groups: [
      { id: 'grain-staples', name: 'Grain Staples', categoryIds: ['cereals'] },
      { id: 'grains-seeds', name: 'Grains & Seeds', categoryIds: ['grains'] },
      { id: 'beans-pulses', name: 'Beans & Pulses', categoryIds: ['legumes', 'pulses'] }
    ]
  },
  {
    id: 'fruits-tree-crops',
    name: 'Fruits & Tree Crops',
    icon: 'Leaf',
    groups: [
      { id: 'fresh-fruits', name: 'Fresh Fruits', categoryIds: ['fruits'] },
      { id: 'nuts-kernels', name: 'Nuts & Kernels', categoryIds: ['nuts'] },
      { id: 'tree-crops', name: 'Tree Crops', categoryIds: ['tree'] }
    ]
  },
  {
    id: 'spices-herbs-medicinal',
    name: 'Spices, Herbs & Medicinal',
    icon: 'Utensils',
    groups: [
      { id: 'spices-seasonings', name: 'Spices & Seasonings', categoryIds: ['spices'] },
      { id: 'herbs-aromatics', name: 'Herbs & Aromatics', categoryIds: ['herbs', 'aromatic'] },
      { id: 'medicinal-botanicals', name: 'Medicinal Botanicals', categoryIds: ['medicinal'] }
    ]
  },
  {
    id: 'industrial-fiber-crops',
    name: 'Industrial & Fiber Crops',
    icon: 'Shield',
    groups: [
      { id: 'fiber-crops', name: 'Fiber Crops', categoryIds: ['fibers'] },
      { id: 'oilseed-crops', name: 'Oilseed Crops', categoryIds: ['oilseeds'] },
      { id: 'bioenergy-feedstocks', name: 'Bioenergy Feedstocks', categoryIds: ['bioenergy'] }
    ]
  },
  {
    id: 'coffee-tea-cocoa',
    name: 'Coffee, Tea & Cocoa',
    icon: 'LineChart',
    groups: [
      { id: 'beverage-crops', name: 'Coffee, Tea & Cocoa', categoryIds: ['beverages'] },
      { id: 'plantation-crops', name: 'Plantation Cash Crops', categoryIds: ['plantation'] },
      { id: 'stimulant-crops', name: 'Stimulant Crops', categoryIds: ['stimulants'] }
    ]
  },
  {
    id: 'animal-feed-forage',
    name: 'Animal Feed & Forage',
    icon: 'HeartHandshake',
    groups: [
      { id: 'forage-fodder', name: 'Forage & Fodder', categoryIds: ['forage'] },
      { id: 'sugar-feedstocks', name: 'Sugar Feedstocks', categoryIds: ['sugar'] }
    ]
  },
  {
    id: 'flowers-specialized',
    name: 'Flowers & Specialized',
    icon: 'BadgeCheck',
    groups: [
      { id: 'flowers-ornamentals', name: 'Flowers & Ornamentals', categoryIds: ['flowers'] },
      { id: 'aquatic-crops', name: 'Aquatic Crops', categoryIds: ['aquatic'] }
    ]
  }
];

const normalizeToken = (value) => String(value || '').toLowerCase().replace(/[^a-z0-9]/g, '');

const uniqueStrings = (values) => {
  const seen = new Set();
  return values.filter((value) => {
    const key = String(value).toLowerCase();
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

const CATEGORY_BY_ID = AGRICULTURAL_CATEGORIES.reduce((acc, category) => {
  acc[category.id] = category;
  return acc;
}, {});

const enrichGroup = (macro, group) => {
  const categories = group.categoryIds.map((id) => CATEGORY_BY_ID[id]).filter(Boolean);
  const items = uniqueStrings(categories.flatMap((category) => category.examples || []));

  return {
    ...group,
    macroCategoryId: macro.id,
    macroCategoryName: macro.name,
    categories,
    items,
    primaryCategoryId: group.categoryIds[0] || ''
  };
};

export const AGRICULTURAL_CATEGORY_HIERARCHY = AGRICULTURAL_MACRO_CATEGORIES.map((macro) => ({
  ...macro,
  groups: macro.groups.map((group) => enrichGroup(macro, group))
}));

const MACRO_BY_ID = AGRICULTURAL_CATEGORY_HIERARCHY.reduce((acc, macro) => {
  acc[macro.id] = macro;
  return acc;
}, {});

const GROUP_BY_ID = AGRICULTURAL_CATEGORY_HIERARCHY.reduce((acc, macro) => {
  macro.groups.forEach((group) => {
    acc[group.id] = group;
  });
  return acc;
}, {});

const CATEGORY_TO_HIERARCHY = AGRICULTURAL_CATEGORY_HIERARCHY.reduce((acc, macro) => {
  macro.groups.forEach((group) => {
    group.categoryIds.forEach((categoryId) => {
      if (!acc[categoryId]) {
        acc[categoryId] = {
          macroCategoryId: macro.id,
          macroCategoryName: macro.name,
          groupId: group.id,
          groupName: group.name
        };
      }
    });
  });
  return acc;
}, {});

const CROP_INDEX = AGRICULTURAL_CATEGORY_HIERARCHY.flatMap((macro) =>
  macro.groups.flatMap((group) =>
    group.categories.flatMap((category) =>
      (category.examples || []).map((crop) => ({
        crop,
        cropKey: normalizeToken(crop),
        categoryId: category.id,
        categoryName: category.name,
        macroCategoryId: macro.id,
        macroCategoryName: macro.name,
        groupId: group.id,
        groupName: group.name
      }))
    )
  )
);

export const resolveCategoryId = (value) => {
  const raw = String(value || '').trim();
  const normalized = normalizeToken(raw);
  if (!normalized) return '';

  const exactById = AGRICULTURAL_CATEGORIES.find((category) => normalizeToken(category.id) === normalized);
  if (exactById) return exactById.id;

  const exactByName = AGRICULTURAL_CATEGORIES.find((category) => normalizeToken(category.name) === normalized);
  if (exactByName) return exactByName.id;

  const byCrop = CROP_INDEX.find((cropEntry) => cropEntry.cropKey === normalized);
  if (byCrop) return byCrop.categoryId;

  return raw;
};

export const getCategoryConfig = (categoryName) => {
  if (!categoryName) {
    return {
      id: 'uncategorized',
      name: 'Uncategorized',
      color: DEFAULT_CATEGORY_COLOR,
      icon: 'FaBox'
    };
  }

  const normalizedCategory = normalizeToken(categoryName);

  const category = AGRICULTURAL_CATEGORIES.find((entry) => {
    const entryId = normalizeToken(entry.id);
    const entryName = normalizeToken(entry.name);
    return (
      normalizedCategory === entryId ||
      normalizedCategory === entryName ||
      normalizedCategory.includes(entryId) ||
      entryId.includes(normalizedCategory) ||
      normalizedCategory.includes(entryName)
    );
  });

  if (category) {
    const hierarchy = CATEGORY_TO_HIERARCHY[category.id];
    return {
      ...category,
      ...hierarchy,
      color: CATEGORY_COLOR_MAP[category.id] || DEFAULT_CATEGORY_COLOR
    };
  }

  const matchedCrop = CROP_INDEX.find((entry) => entry.cropKey === normalizedCategory);
  if (matchedCrop) {
    const fallbackCategory = CATEGORY_BY_ID[matchedCrop.categoryId];
    return {
      ...fallbackCategory,
      macroCategoryId: matchedCrop.macroCategoryId,
      macroCategoryName: matchedCrop.macroCategoryName,
      groupId: matchedCrop.groupId,
      groupName: matchedCrop.groupName,
      color: CATEGORY_COLOR_MAP[fallbackCategory.id] || DEFAULT_CATEGORY_COLOR
    };
  }

  const matchedMacro = AGRICULTURAL_CATEGORY_HIERARCHY.find((macro) => {
    const macroId = normalizeToken(macro.id);
    const macroName = normalizeToken(macro.name);
    return normalizedCategory === macroId || normalizedCategory === macroName || macroName.includes(normalizedCategory);
  });

  if (matchedMacro) {
    return {
      id: matchedMacro.id,
      name: matchedMacro.name,
      macroCategoryId: matchedMacro.id,
      macroCategoryName: matchedMacro.name,
      color: DEFAULT_CATEGORY_COLOR,
      icon: matchedMacro.icon || 'FaBox'
    };
  }

  return {
    id: 'custom',
    name: categoryName,
    color: DEFAULT_CATEGORY_COLOR,
    icon: 'FaBox'
  };
};

export const getCategorySuggestions = (searchTerm = '') => {
  const term = normalizeToken(searchTerm);
  if (!term) return AGRICULTURAL_CATEGORIES;

  return AGRICULTURAL_CATEGORIES
    .map((category) => {
      const categoryName = normalizeToken(category.name);
      const categoryId = normalizeToken(category.id);
      const hierarchy = CATEGORY_TO_HIERARCHY[category.id];
      const macro = hierarchy ? MACRO_BY_ID[hierarchy.macroCategoryId] : null;
      const group = hierarchy ? GROUP_BY_ID[hierarchy.groupId] : null;

      let score = 0;
      if (categoryId === term || categoryName === term) score += 200;
      if (categoryName.startsWith(term)) score += 120;
      if (categoryName.includes(term)) score += 80;
      if (categoryId.includes(term)) score += 70;
      if (macro && normalizeToken(macro.name).includes(term)) score += 45;
      if (group && normalizeToken(group.name).includes(term)) score += 35;

      const cropMatchScore = (category.examples || []).reduce((max, crop) => {
        const cropToken = normalizeToken(crop);
        if (cropToken === term) return Math.max(max, 160);
        if (cropToken.startsWith(term)) return Math.max(max, 110);
        if (cropToken.includes(term)) return Math.max(max, 60);
        return max;
      }, 0);

      score += cropMatchScore;

      return { category, score };
    })
    .filter((entry) => entry.score > 0)
    .sort((a, b) => b.score - a.score || a.category.name.localeCompare(b.category.name))
    .map((entry) => {
      const hierarchy = CATEGORY_TO_HIERARCHY[entry.category.id] || {};
      return {
        ...entry.category,
        ...hierarchy,
        color: CATEGORY_COLOR_MAP[entry.category.id] || DEFAULT_CATEGORY_COLOR
      };
    });
};

export const searchCrops = (query = '', options = {}) => {
  const term = normalizeToken(query);
  if (!term) return [];

  const limit = Number.isFinite(options.limit) ? Number(options.limit) : 30;
  const withContext = options.withContext === true;

  const ranked = CROP_INDEX
    .map((entry) => {
      let score = 0;
      if (entry.cropKey === term) score = 300;
      else if (entry.cropKey.startsWith(term)) score = 220;
      else if (entry.cropKey.includes(term)) score = 140;
      else if (normalizeToken(entry.categoryName).includes(term)) score = 80;
      else if (normalizeToken(entry.groupName).includes(term)) score = 70;
      else if (normalizeToken(entry.macroCategoryName).includes(term)) score = 60;

      return score > 0 ? { ...entry, score } : null;
    })
    .filter(Boolean)
    .sort((a, b) => b.score - a.score || a.crop.localeCompare(b.crop));

  if (withContext) {
    return ranked.slice(0, limit).map(({ score, ...entry }) => entry);
  }

  const names = [];
  const seen = new Set();
  ranked.forEach((entry) => {
    const key = entry.crop.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    names.push(entry.crop);
  });

  return names.slice(0, limit);
};

export const getMacroCategoryById = (macroCategoryId) =>
  AGRICULTURAL_CATEGORY_HIERARCHY.find((macro) => macro.id === macroCategoryId) || null;

export const getGroupById = (groupId) => GROUP_BY_ID[groupId] || null;

export const getCategoryHierarchyMeta = (categoryId) => CATEGORY_TO_HIERARCHY[categoryId] || null;